import { Control, useFormContext, useWatch } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import StyledInput from '../styled/StyledInput';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import StyledRadioGroup from '../styled/StyledRadioGroup';
import { Label } from '../ui/label';
import { useTranslation } from 'react-i18next';
import StyledTranslatableInput from '../styled/StyledTranslatableInput';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledTranslatableTextarea from '../styled/StyledTranslatableTextarea';
import { useMemo } from 'react';
import { CarRentalFormDataWithTranslations } from '@/i18n/translation-type-utils';

interface InsuranceCardProps {
  control: Control<CarRentalFormDataWithTranslations>;
  index: number;
  language: SupportedLocale;
  deleteInsurance: () => void;
  isViewMode?: boolean;
}

const InsuranceCard = ({
  control,
  index,
  language,
  deleteInsurance,
  isViewMode = false,
}: InsuranceCardProps) => {
  const { t } = useTranslation();
  const currentTitle = useWatch({
    control,
    name: `insurances.${index}.name`,
  });
  const translations = useWatch({
    control,
    name: 'translations',
  });
  const { setValue } = useFormContext<CarRentalFormDataWithTranslations>();

  const cardTitle = useMemo(() => {
    const currentTranslation = translations?.[language]?.insurances?.[index];
    const currentTitleTranslated = currentTranslation?.name ?? currentTitle;
    return (
      currentTitleTranslated ||
      currentTitle ||
      t('carRental.insurance.insurance', { index: index + 1 })
    );
  }, [currentTitle, index, language, t, translations]);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle className="text-base leading-6 hover:cursor-pointer">
          {cardTitle}
        </CardTitle>
        <div className="flex gap-10">
          <div className="flex gap-4">
            {index > 0 && !isViewMode && (
              <StyledTooltipContainer
                tooltipMessage={t('carRental.insurance.delete')}
              >
                <TrashIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={() => deleteInsurance()}
                />
              </StyledTooltipContainer>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <StyledTranslatableInput
            originalName={`insurances.${index}.name`}
            control={control}
            title={t('carRental.insurance.name')}
            name={`insurances.${index}.name`}
            setValue={setValue}
            language={language}
            placeholder={t('carRental.insurance.namePlaceholder')}
            disabled={isViewMode}
          />
          <StyledTranslatableInput
            originalName={`insurances.${index}.shortDescription`}
            control={control}
            title={t('carRental.insurance.shortDescription')}
            name={`insurances.${index}.shortDescription`}
            setValue={setValue}
            language={language}
            placeholder={t('carRental.insurance.shortDescriptionPlaceholder')}
            disabled={isViewMode}
          />
          <StyledTranslatableTextarea
            originalName={`insurances.${index}.longDescription`}
            control={control}
            title={t('carRental.insurance.longDescription')}
            name={`insurances.${index}.longDescription`}
            setValue={setValue}
            language={language}
            placeholder={t('carRental.insurance.longDescriptionPlaceholder')}
            rows={3}
            disabled={isViewMode}
          />
          <div className="flex flex-col gap-1">
            <Label className="text-gray-700">
              {t('carRental.insurance.amount')}
            </Label>
            <div className="flex gap-6 w-full items-center">
              <div className="min-w-20 max-w-40">
                <StyledInput
                  control={control}
                  name={`insurances.${index}.amount`}
                  placeholder={t('carRental.insurance.amountPlaceholder')}
                  type="number"
                  min={0}
                  disabled={isViewMode}
                />
              </div>
              <StyledRadioGroup
                control={control}
                name={`insurances.${index}.billingType`}
                options={[
                  {
                    label: t('carRental.insurance.billingTypePerDay'),
                    value: 'perDay',
                  },
                  {
                    label: t('carRental.insurance.billingTypePerRental'),
                    value: 'perRental',
                  },
                ]}
                className="w-1/2"
                disabled={isViewMode}
              />
            </div>
            <p className="text-sm text-gray-500">
              {t('carRental.insurance.billingTypeNote')}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InsuranceCard;
